// xG Table — data + row shaping for the widget.
//
// Unlike League/Form, xG has its own columns: expected-goals metrics with
// signed "Diff" cells that carry a colour (green when good, red when bad) and a
// `data-val` used for sorting, plus display text with the sign stripped. Ported
// verbatim from the legacy renderer (renderers-tables.js) so the React output
// matches; the A/B guards it.
//
// MIGRATION FRICTION: xgTableData.json is dummy data extracted from the legacy
// data.js and imported as JSON (component stays pure). Remove at app-api time.

import { teamLabel } from "./leagueTable.js";
import type { SortableColumn } from "../table/sort.js";
import rawData from "./xgTableData.json" with { type: "json" };

// [name, P, xGF, xGF-diff, xGA, xGA-diff, xGD, xGD-diff, xPts]. Diff/metric
// fields are strings ("+0.12", "2.48") exactly as the legacy stored them.
export type XgTeam = readonly [
  string,
  number,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
];

export const XG_TABLE_DATA = rawData as unknown as Record<string, readonly XgTeam[]>;

export const XG_DEFAULT_RATE = "Per Game";
export const XG_DEFAULT_TIMELINE = "5";

/** Teams for a rate + timeline key ("Per Game-5"); legacy fallback "Per Game-5". */
export function getXgTeams(rate: string, timeline: string): readonly XgTeam[] {
  return XG_TABLE_DATA[`${rate}-${timeline}`] ?? XG_TABLE_DATA["Per Game-5"]!;
}

export interface XgColumn extends SortableColumn {
  label: string;
  cls: string;
}

// The three "Diff" columns share a label but sort independently (distinct keys).
// `xga` is lowest-is-best (first click ascending), matching the legacy
// SORT_LOW_FIRST set; the Diff columns default descending.
export const XG_COLUMNS: readonly XgColumn[] = [
  { key: "team", label: "Team", type: "text", cls: "col-team" },
  { key: "p", label: "P", type: "num", cls: "col-num col-opt" },
  { key: "xgf", label: "xGF", type: "num", cls: "col-num col-opt" },
  { key: "xgfDiff", label: "Diff", type: "num", cls: "col-num col-delta" },
  { key: "xga", label: "xGA", type: "num", lowFirst: true, cls: "col-num col-opt" },
  { key: "xgaDiff", label: "Diff", type: "num", cls: "col-num col-delta" },
  { key: "xgd", label: "xGD", type: "num", cls: "col-num col-diff" },
  { key: "xgdDiff", label: "Diff", type: "num", cls: "col-num col-delta" },
  { key: "xpts", label: "xPts", type: "num", cls: "col-num" },
];

/** A signed metric shown coloured: text (sign stripped), the raw signed value
 *  for sorting, and the colour class. */
export interface XgDelta {
  text: string;
  dataVal: string;
  cls: "xg-pos" | "xg-neg";
}

/** xGD cell: keeps a leading minus, drops a leading plus; raw value sorts. */
export interface XgDiff {
  text: string;
  dataVal: string;
}

export interface XgRow {
  name: string;
  label: string;
  highlighted: boolean;
  p: number;
  xgf: string;
  xga: string;
  xpts: string;
  xgfDiff: XgDelta;
  xgaDiff: XgDelta;
  xgd: XgDiff;
  xgdDiff: XgDelta;
}

// Good/bad colour: a positive attacking/overall diff is good (xg-pos); for the
// conceding metric (xGA) it flips — conceding fewer than expected is good.
function xgCls(s: string, isConceding: boolean): "xg-pos" | "xg-neg" {
  const isPos = s.charAt(0) === "+";
  return (isConceding ? !isPos : isPos) ? "xg-pos" : "xg-neg";
}

function delta(s: string, isConceding: boolean): XgDelta {
  return { text: s.replace(/^[+-]/, ""), dataVal: s, cls: xgCls(s, isConceding) };
}

function diff(s: string): XgDiff {
  return { text: s.replace(/^\+/, ""), dataVal: s };
}

/** Shape raw xG tuples into display rows for a fixture (highlight + label). */
export function shapeXgRows(
  teams: readonly XgTeam[],
  fixture: string,
  isMobile: boolean
): XgRow[] {
  const parts = (fixture || "Arsenal v Everton").split(" v ");
  const t1 = (parts[0] ?? "").trim().toLowerCase();
  const t2 = (parts[1] ?? "").trim().toLowerCase();
  return teams.map((t) => {
    const lower = t[0].toLowerCase();
    return {
      name: t[0],
      label: teamLabel(t[0], isMobile),
      highlighted:
        (t1 !== "" && lower.indexOf(t1) !== -1) ||
        (t2 !== "" && lower.indexOf(t2) !== -1),
      p: t[1],
      xgf: t[2],
      xgfDiff: delta(t[3], false),
      xga: t[4],
      xgaDiff: delta(t[5], true),
      xgd: diff(t[6]),
      xgdDiff: delta(t[7], false),
      xpts: t[8],
    };
  });
}
